import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { adminApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  useToast,
} from '@/components/ui';
import { Fact, FactList } from '@/features/admin/Facts';
import { RecordHeader } from '@/features/admin/RecordHeader';
import { PlanFormModal } from '@/features/billing/PlanFormModal';
import { studentSubscriptionPath } from '@/features/billing/links';
import {
  entitlementLines,
  formatMoney,
  formatPrice,
  isStudentPlan,
  planGrantLines,
  statusLabel,
  statusTone,
} from '@/features/billing/money';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate } from '@/utils/format';

/**
 * One plan, whole: what it promises, who bought it, and what it has billed.
 *
 * The catalogue answers "what is on sale". This answers the question asked
 * before changing one — how many people a price change reaches, who is on
 * their way off it, and whether the money says the price is right.
 */
export function AdminPlanDetailPage() {
  const { code } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [retiring, setRetiring] = useState(false);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['admin-plan', code],
    queryFn: () => adminApi.getBillingPlan(code),
  });

  const isStudent = isStudentPlan(data);
  // Student subscribers are their own roster; the plan payload carries only
  // company rows, so a student plan asks the student roster for its own.
  const students = useQuery({
    queryKey: ['admin-student-subscriptions', { plan_id: data?.id, page_size: 20 }],
    queryFn: () => adminApi.listStudentSubscriptions({ plan_id: data.id, page_size: 20 }),
    enabled: isStudent,
  });

  const save = useMutation({
    mutationFn: ({ values }) => adminApi.updateBillingPlan(code, values),
    onSuccess: (_result, variables) => {
      toast.success(variables.message);
      setRetiring(false);
      ['admin-plan', 'admin-plans', 'admin-billing-overview', 'public-plans'].forEach(
        (key) => void queryClient.invalidateQueries({ queryKey: [key] }),
      );
    },
    onError: (saveError) => toast.error(errorMessage(saveError, 'Could not update the plan')),
  });

  if (isPending) return <LoadingState label="Loading the plan…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const onSale = data.is_active && data.is_public && !data.is_free_plan;
  const subscriberRows = isStudent
    ? (students.data?.data ?? []).map((row) => ({
        id: row.id,
        name: row.student_name,
        to: studentSubscriptionPath(row.id),
        status: row.status,
        cancel_at_period_end: row.cancel_at_period_end,
        scheduled_plan_name: null,
        current_period_end: row.current_period_end,
        outstanding_cents: row.outstanding_cents,
        currency: row.currency,
      }))
    : data.subscribers.map((row) => ({
        ...row,
        name: row.company_name,
        to: paths.admin.subscriptionDetail(row.id),
      }));
  const audienceNoun = isStudent ? 'student' : 'company';
  const byStatus = Object.entries(data.subscribers_by_status ?? {}).filter(
    ([, count]) => count > 0,
  );

  return (
    <div>
      <RecordHeader
        backTo={paths.admin.plans}
        backLabel="Plans"
        eyebrow="Plan"
        title={data.name}
        subtitle={[
          data.code,
          data.is_free_plan
            ? 'no charge'
            : formatPrice(data.price_cents, data.currency, data.interval),
          `${data.subscriber_count} ${data.subscriber_count === 1 ? 'subscriber' : 'subscribers'}`,
        ].join(' · ')}
        badges={
          <>
            <Badge tone={isStudent ? 'brand' : 'neutral'}>
              {isStudent ? 'Students' : 'Companies'}
            </Badge>
            {onSale && <Badge tone="success">On sale</Badge>}
            {data.is_free_plan && <Badge tone="neutral">Not sold</Badge>}
            {!data.is_public && <Badge tone="warning">Private</Badge>}
            {!data.is_active && <Badge>Retired</Badge>}
            {data.entitlements_drifted && <Badge tone="danger">Out of step</Badge>}
          </>
        }
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              isLoading={save.isPending}
              onClick={() =>
                save.mutate({
                  values: { is_public: !data.is_public },
                  message: data.is_public
                    ? `${data.name} is hidden from the pricing page`
                    : `${data.name} is on the pricing page`,
                })
              }
            >
              {data.is_public ? 'Hide' : 'Publish'}
            </Button>
            {/* Retire is switched off for now; Restore stays for plans retired
                before. To bring it back, render in place of `null`:
                <Button variant="ghostDanger" size="sm"
                  onClick={() => data.subscriber_count > 0 ? setRetiring(true)
                    : save.mutate({ values: { is_active: false },
                        message: `${data.name} is retired` })}>
                  Retire
                </Button> */}
            {data.is_active ? null : (
              <Button
                variant="ghost"
                size="sm"
                isLoading={save.isPending}
                onClick={() =>
                  save.mutate({
                    values: { is_active: true },
                    message: `${data.name} is back on the menu`,
                  })
                }
              >
                Restore
              </Button>
            )}
          </>
        }
        facts={[
          {
            label: 'Subscribers',
            value: data.subscriber_count,
            hint: `${data.leaving_count} leaving · ${data.arriving_count} arriving`,
          },
          {
            label: 'Monthly recurring',
            value: formatMoney(data.mrr_cents, data.currency),
            hint: data.interval === 'YEARLY' ? 'Yearly price divided down' : 'At the current price',
          },
          {
            label: 'Outstanding',
            value: formatMoney(data.outstanding_cents, data.currency),
            hint: `${data.invoices_issued} invoice${data.invoices_issued === 1 ? '' : 's'} issued`,
          },
          {
            label: 'Created',
            value: formatDate(data.created_at),
            hint: `Last edited ${formatDate(data.updated_at)}`,
          },
        ]}
      />

      {data.entitlements_drifted && (
        <Alert tone="warning" title="This row no longer matches what free grants" className="mb-6">
          Free limits are a constant in code, so that authorisation never depends on whether anybody
          ran a seed script. This row is what the pricing page prints — and right now it is printing
          a promise nothing enforces.
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader
              title="What it grants"
              description="Every line is enforced somewhere. A bullet no code honours is a lie on a pricing page."
              action={
                <span className="text-ink-muted text-sm">
                  {data.is_free_plan
                    ? 'No charge'
                    : formatPrice(data.price_cents, data.currency, data.interval)}
                </span>
              }
            />
            {data.description && <p className="text-ink-muted mb-4 text-sm">{data.description}</p>}

            {data.enforced_entitlements ? (
              /* On the free plan the row and the rule are two different
                 things, so they are shown as two columns rather than one
                 list that would have to pick a side. */
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <p className="text-ink text-sm font-semibold">As described here</p>
                  <p className="text-ink-muted text-xs">Printed on the pricing page</p>
                  <ul className="text-ink-muted mt-2 space-y-1 text-sm">
                    {entitlementLines(data.entitlements).map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-ink text-sm font-semibold">As enforced</p>
                  <p className="text-ink-muted text-xs">What a company with no subscription gets</p>
                  <ul className="text-ink-muted mt-2 space-y-1 text-sm">
                    {entitlementLines(data.enforced_entitlements).map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <ul className="text-ink-muted grid gap-1.5 text-sm sm:grid-cols-2">
                {planGrantLines(data).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Subscribers"
              description={
                subscriberRows.length < data.subscriber_count
                  ? `The ${subscriberRows.length} most recent of ${data.subscriber_count}.`
                  : undefined
              }
              action={
                data.subscriber_count > 0 ? (
                  <Link
                    to={`${paths.admin.subscriptions}?plan_id=${data.id}${
                      isStudent ? '&customer=student' : ''
                    }`}
                    className="text-brand-700 text-sm hover:underline"
                  >
                    All of them
                  </Link>
                ) : null
              }
            />
            {isStudent && students.isError ? (
              <Alert tone="error" title="Could not load the subscribers">
                {errorMessage(students.error)}
              </Alert>
            ) : subscriberRows.length === 0 ? (
              <EmptyState
                compact
                title={
                  data.is_free_plan || data.price_cents === 0
                    ? 'Nobody subscribes to this plan'
                    : 'Nobody is on this plan'
                }
                description={
                  data.is_free_plan || data.price_cents === 0
                    ? `A ${audienceNoun} is on it by having no paid plan at all.`
                    : undefined
                }
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table w-full text-sm">
                  <thead className="text-ink-muted text-left">
                    <tr className="border-line border-b">
                      <th scope="col" className="py-2 pr-4 font-medium">
                        {isStudent ? 'Student' : 'Company'}
                      </th>
                      <th scope="col" className="py-2 pr-4 font-medium">
                        Status
                      </th>
                      <th scope="col" className="py-2 pr-4 font-medium">
                        Renews
                      </th>
                      <th scope="col" className="py-2 pr-4 text-right font-medium">
                        Outstanding
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriberRows.map((row) => (
                      <tr key={row.id} className="border-line border-b last:border-0">
                        <td className="py-3 pr-4">
                          <Link to={row.to} className="text-ink font-medium hover:underline">
                            {row.name}
                          </Link>
                        </td>
                        <td className="py-3 pr-4">
                          <Badge tone={statusTone(row.status)}>{statusLabel(row.status)}</Badge>
                          {(row.cancel_at_period_end || row.scheduled_plan_name) && (
                            <span className="text-ink-muted mt-1 block text-xs">
                              {row.cancel_at_period_end ? (
                                'Ending'
                              ) : (
                                <span className="inline-flex items-center gap-1">
                                  <Icon name="arrowRight" size={13} />
                                  {row.scheduled_plan_name}
                                </span>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="text-ink-muted py-3 pr-4 whitespace-nowrap">
                          {formatDate(row.current_period_end)}
                        </td>
                        <td className="py-3 pr-4 text-right tabular-nums">
                          {row.outstanding_cents > 0 ? (
                            <span className="text-ink">
                              {formatMoney(row.outstanding_cents, row.currency)}
                            </span>
                          ) : (
                            <span className="text-ink-muted">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Availability" />
            {/* Two independent switches, and the difference between them is
                the whole point: retiring closes the door, hiding moves it off
                the street. */}
            <p className="text-ink-muted text-sm">
              {data.is_active
                ? data.is_public
                  ? 'On the pricing page and open to anybody.'
                  : 'Works, but not listed — this is how a negotiated price exists.'
                : 'Retired. Nobody new can subscribe; everybody already on it keeps it.'}
            </p>
            <FactList columns={1} className="mt-4">
              <Fact label="Code" value={data.code} />
              <Fact
                label="Price"
                value={formatPrice(data.price_cents, data.currency, data.interval)}
              />
              <Fact label="Currency" value={data.currency} />
              <Fact label="Sort order" value={data.sort_order} showEmpty />
              <Fact label="Created" value={formatDate(data.created_at)} />
              <Fact label="Last edited" value={formatDate(data.updated_at)} />
            </FactList>
          </Card>

          <Card>
            <CardHeader title="Movement" />
            {byStatus.length === 0 ? (
              <EmptyState compact title="No subscriptions have ever been on it" />
            ) : (
              <dl className="space-y-1.5 text-sm">
                {byStatus.map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between gap-3">
                    <dt className="text-ink-muted">{statusLabel(status)}</dt>
                    <dd className="text-ink font-medium tabular-nums">{count}</dd>
                  </div>
                ))}
              </dl>
            )}
            <dl className="border-line mt-3 space-y-1.5 border-t pt-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-ink-muted">Leaving at period end</dt>
                <dd className="text-ink font-medium tabular-nums">{data.leaving_count}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-ink-muted">Arriving at period end</dt>
                <dd className="text-ink font-medium tabular-nums">{data.arriving_count}</dd>
              </div>
            </dl>
          </Card>

          <Card>
            <CardHeader title="Billed" />
            {/* From the invoices' own copy of the plan code, so re-pricing a
                plan never rewrites what it charged last quarter. */}
            <dl className="space-y-1.5 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-ink-muted">Invoices issued</dt>
                <dd className="text-ink font-medium tabular-nums">{data.invoices_issued}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-ink-muted">Invoiced</dt>
                <dd className="text-ink font-medium tabular-nums">
                  {formatMoney(data.invoiced_cents, data.currency)}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-ink-muted">Paid</dt>
                <dd className="text-ink font-medium tabular-nums">
                  {formatMoney(data.paid_cents, data.currency)}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-ink-muted">Outstanding</dt>
                <dd className="text-ink font-medium tabular-nums">
                  {formatMoney(data.outstanding_cents, data.currency)}
                </dd>
              </div>
            </dl>
            <p className="text-ink-muted mt-3 text-xs">
              Historic: each invoice kept the plan code it was issued under, so re-pricing never
              rewrites what was charged.
            </p>
          </Card>
        </div>
      </div>

      <PlanFormModal open={editing} plan={data} onClose={() => setEditing(false)} />

      <ConfirmDialog
        open={retiring}
        title={`Retire ${data.name}`}
        message={`${data.subscriber_count} ${
          isStudent
            ? data.subscriber_count === 1
              ? 'student stays'
              : 'students stay'
            : data.subscriber_count === 1
              ? 'company stays'
              : 'companies stay'
        } on it and keep everything it grants — retiring only takes it off the menu, so nobody new can subscribe. It can be restored at any time.`}
        confirmLabel="Retire plan"
        isLoading={save.isPending}
        onConfirm={() =>
          save.mutate({ values: { is_active: false }, message: `${data.name} is retired` })
        }
        onCancel={() => setRetiring(false)}
      />
    </div>
  );
}
